import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { Global } from "../helpers/Global";

export const OrdenAdmin = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [mensaje, setMensaje] = useState("");

  // ordenación
  const [ordenCampo, setOrdenCampo] = useState({ campo: "id", asc: true });

  // filtros
  const [filtroClienteId, setFiltroClienteId] = useState("");
  const [mostrarFinalizadas, setMostrarFinalizadas] = useState(true);
  const [mostrarCanceladas, setMostrarCanceladas] = useState(true);
  const [mostrarEliminadas, setMostrarEliminadas] = useState(false);

  // modales
  const [modalEstado, setModalEstado] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState("");

  // edición
  const [editTitulo, setEditTitulo] = useState("");
  const [editDescripcion, setEditDescripcion] = useState("");
  const [editCliente, setEditCliente] = useState("");
  const [editCentros, setEditCentros] = useState([]);

  useEffect(() => {
    fetchClientes();
    fetchOrdenes();
  }, []);

  useEffect(() => {
    if (editCliente) fetchCentros(editCliente);
  }, [editCliente]);

  const fetchClientes = async () => {
    try {
      const res = await fetch(Global.url + "clientes", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      setClientes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al obtener clientes:", err);
    }
  };

  const fetchCentros = async (clienteId) => {
    try {
      const res = await fetch(`${Global.url}centros/cliente/${clienteId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      setCentros(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al obtener centros:", err);
    }
  };

  const fetchOrdenes = async () => {
    try {
      const res = await fetch(Global.url + "ordenes", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      setOrdenes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al obtener órdenes:", err);
      setMensaje("No se pudieron cargar las órdenes.");
    }
  };

  const cambiarEstado = async () => {
    try {
      const res = await fetch(`${Global.url}ordenes/estado/${ordenSeleccionada.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      if (res.ok) {
        fetchOrdenes();
        setModalEstado(false);
      } else {
        console.error("Error al actualizar estado:", await res.text());
      }
    } catch (err) {
      console.error("Error al actualizar estado:", err);
    }
  };

  const eliminarOrden = async (id) => {
    if (!window.confirm("¿Eliminar esta orden de trabajo?")) return;
    try {
      const res = await fetch(Global.url + "ordenes/" + id, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) fetchOrdenes();
      else console.error("Error al eliminar orden:", await res.text());
    } catch (err) {
      console.error("Error al eliminar orden:", err);
    }
  };

  const restaurarOrden = async (id) => {
    try {
      const res = await fetch(Global.url + "ordenes/" + id, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ activo: true }),
      });
      if (res.ok) fetchOrdenes();
      else console.error("Error al restaurar orden:", await res.text());
    } catch (err) {
      console.error("Error al restaurar orden:", err);
    }
  };

  const abrirModalEditar = async (orden) => {
    setOrdenSeleccionada(orden);
    setEditTitulo(orden.titulo || "");
    setEditDescripcion(orden.descripcion || "");
    setEditCliente(orden.cliente_id || "");

    try {
      const res = await fetch(`${Global.url}ordenes/${orden.id}/centros`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      setEditCentros((Array.isArray(data) ? data : []).map((c) => c.id));
    } catch (err) {
      console.error("Error al obtener centros asociados:", err);
      setEditCentros([]);
    }
    setModalEditar(true);
  };

  const toggleCentro = (centroId) => {
    setEditCentros((prev) =>
      prev.includes(centroId) ? prev.filter((id) => id !== centroId) : [...prev, centroId]
    );
  };

  const guardarCambios = async () => {
    try {
      const res = await fetch(`${Global.url}ordenes/${ordenSeleccionada.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          cliente_id: editCliente,
          titulo: editTitulo,
          descripcion: editDescripcion,
          centros_id: editCentros,
        }),
      });
      if (!res.ok) {
        console.error("Error al actualizar orden:", await res.text());
        return;
      }
      fetchOrdenes();
      setModalEditar(false);
    } catch (err) {
      console.error("Error al guardar cambios:", err);
    }
  };

  const manejarOrden = (campo) => {
    setOrdenCampo((prev) => ({
      campo,
      asc: prev.campo === campo ? !prev.asc : true,
    }));
  };

  // ---------- opciones y filtros ----------
  const clienteOptions = useMemo(
    () => [{ value: "", label: "Todos" }, ...clientes.map((c) => ({ value: c.id, label: c.nombre }))],
    [clientes]
  );

  const clienteValue =
    filtroClienteId !== ""
      ? {
          value: filtroClienteId,
          label: clientes.find((c) => c.id === Number(filtroClienteId))?.nombre || "",
        }
      : { value: "", label: "Todos" };

  const ordenesFiltradas = useMemo(() => {
    return ordenes.filter((ot) => {
      const matchCliente = !filtroClienteId || ot.cliente_id === Number(filtroClienteId);
      const visibleFinalizada = mostrarFinalizadas || (ot.estado || "").toLowerCase() !== "finalizada";
      const visibleCancelada = mostrarCanceladas || (ot.estado || "").toLowerCase() !== "cancelada";
      const visibleActiva = mostrarEliminadas || ot.activo !== false;
      return matchCliente && visibleFinalizada && visibleCancelada && visibleActiva;
    });
  }, [ordenes, filtroClienteId, mostrarFinalizadas, mostrarCanceladas, mostrarEliminadas]);

  const ordenesOrdenadas = useMemo(() => {
    return [...ordenesFiltradas].sort((a, b) => {
      const valA = a[ordenCampo.campo] ?? "";
      const valB = b[ordenCampo.campo] ?? "";
      if (typeof valA === "string" && typeof valB === "string") {
        return ordenCampo.asc
          ? valA.localeCompare(valB, undefined, { sensitivity: "base" })
          : valB.localeCompare(valA, undefined, { sensitivity: "base" });
      }
      return ordenCampo.asc ? valA - valB : valB - valA;
    });
  }, [ordenesFiltradas, ordenCampo]);

  // ---------- render ----------
  return (
    <div className="consulta-ticajes">
      <h2 className="centrar-titulo">Órdenes de Trabajo</h2>
      {mensaje && <p className="rojo">{mensaje}</p>}

      {/* Filtros con el mismo layout que el resto de listados */}
      <div className="filtros">
        <div className="filtro">
          <label>Cliente:</label>
          <Select
            options={clienteOptions}
            value={clienteValue}
            onChange={(opt) => setFiltroClienteId(opt?.value || "")}
            isClearable
            placeholder="Todos"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro filtro-checkbox">
          <label htmlFor="chk-fin">Mostrar finalizadas</label>
          <input
            id="chk-fin"
            type="checkbox"
            checked={mostrarFinalizadas}
            onChange={(e) => setMostrarFinalizadas(e.target.checked)}
          />
        </div>

        <div className="filtro filtro-checkbox">
          <label htmlFor="chk-can">Mostrar canceladas</label>
          <input
            id="chk-can"
            type="checkbox"
            checked={mostrarCanceladas}
            onChange={(e) => setMostrarCanceladas(e.target.checked)}
          />
        </div>

        <div className="filtro filtro-checkbox">
          <label htmlFor="chk-del">Mostrar eliminadas</label>
          <input
            id="chk-del"
            type="checkbox"
            checked={mostrarEliminadas}
            onChange={(e) => setMostrarEliminadas(e.target.checked)}
          />
        </div>
      </div>

      {/* Tabla (mismo contenedor que usas en otros listados) */}
      <div className="table-listado-padre">
        <table className="table-listado orden-trabajo">
          <thead>
            <tr>
              <th onClick={() => manejarOrden("id")}>ID</th>
              <th onClick={() => manejarOrden("cliente_nombre")}>Cliente</th>
              <th onClick={() => manejarOrden("titulo")}>Título</th>
              <th onClick={() => manejarOrden("descripcion")}>Descripción</th>
              <th onClick={() => manejarOrden("estado")}>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {ordenesOrdenadas.map((ot) => (
              <tr key={ot.id} style={{ opacity: ot.activo === false ? 0.5 : 1 }}>
                <td>{ot.id}</td>
                <td>{ot.cliente_nombre}</td>
                <td>{ot.titulo}</td>
                <td>{ot.descripcion}</td>
                <td>{ot.estado}</td>
                <td>
                  {ot.activo === false ? (
                    <button className="btn-mapa" onClick={() => restaurarOrden(ot.id)}>
                      Restaurar
                    </button>
                  ) : (
                    <>
                      <button
                        className="btn-mapa"
                        onClick={() => {
                          setOrdenSeleccionada(ot);
                          setNuevoEstado(ot.estado);
                          setModalEstado(true);
                        }}
                      >
                        Cambiar Estado
                      </button>
                      <button
                        className="btn-mapa"
                        onClick={() => abrirModalEditar(ot)}
                        style={{ margin: "0 0.5em" }}
                      >
                        Modificar
                      </button>
                      <button className="btn-mapa" onClick={() => eliminarOrden(ot.id)}>
                        Eliminar
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de estado */}
      {modalEstado && ordenSeleccionada && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setModalEstado(false)}>
              &times;
            </button>
            <h3>Cambiar estado de la orden #{ordenSeleccionada.id}</h3>
            <select
              className="input-layout"
              value={nuevoEstado}
              onChange={(e) => setNuevoEstado(e.target.value)}
            >
              <option value="pendiente">Pendiente</option>
              <option value="en curso">En curso</option>
              <option value="finalizada">Finalizada</option>
              <option value="cancelada">Cancelada</option>
            </select>
            <button className="btn-login" onClick={cambiarEstado}>Guardar</button>
          </div>
        </div>
      )}

      {/* Modal de edición */}
      {modalEditar && ordenSeleccionada && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setModalEditar(false)}>
              &times;
            </button>
            <h3>Editar Orden #{ordenSeleccionada.id}</h3>

            <label>Título:</label>
            <input className="input-layout" value={editTitulo} onChange={(e) => setEditTitulo(e.target.value)} />

            <label>Descripción:</label>
            <textarea className="input-layout" value={editDescripcion} onChange={(e) => setEditDescripcion(e.target.value)} />

            <label>Cliente:</label>
            <select
              className="input-layout"
              value={editCliente}
              onChange={(e) => setEditCliente(e.target.value)}
            >
              <option value="">Selecciona un cliente</option>
              {clientes.map((cli) => (
                <option key={cli.id} value={cli.id}>{cli.nombre}</option>
              ))}
            </select>

            {centros.length > 0 && (
              <>
                <label>Centros:</label>
                <div className="checkbox-group">
                  {centros.map((c) => (
                    <label key={c.id}>
                      <input
                        type="checkbox"
                        value={c.id}
                        checked={editCentros.includes(c.id)}
                        onChange={() => toggleCentro(c.id)}
                      />
                      {c.nombre}
                    </label>
                  ))}
                </div>
              </>
            )}

            <button className="btn-login" onClick={guardarCambios}>💾 Guardar Cambios</button>
          </div>
        </div>
      )}
    </div>
  );
};
